import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  IoArrowBackOutline,
  IoChevronBackOutline,
  IoChevronForwardOutline,
  IoLocationOutline,
  IoCalendarOutline,
  IoTimeOutline,
  IoAlertCircleOutline,
  IoSpeedometerOutline,
  IoCheckmarkCircleOutline,
  IoDocumentTextOutline,
  IoTimerOutline,
  IoChatbubbleEllipsesOutline,
  IoDocumentAttachOutline,
  IoSendOutline,
  IoCloseOutline,
  IoAttachOutline,
  IoStarOutline,
  IoRefreshOutline,
  IoEyeOutline,
  IoEyeOffOutline,
} from "react-icons/io5";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import { BadgeStatus } from "../../../components/BadgeStatus";
import { EvaluationModal } from "@/components/EvaluationModal";
import { ReopenModal } from "@/components/ReopenModal";
import {
  useGetOcurrenceById,
  useCanReopenOcurrence,
} from "@/hooks/useOcurrences";
import { useCreateComment } from "@/hooks/useComments";
import { useGeneratePresignedUrl } from "@/hooks/useMedia";
import {
  useCanEvaluate,
  useGetEvaluationsByOccurrence,
} from "@/hooks/useEvaluations";
import {
  useFollowOcurrence,
  useUnfollowOcurrence,
  useIsFollowingOcurrence,
} from "@/hooks/useDuplicateCheck";
import { useGetCurrentUser } from "@/hooks/useUser";
import type { Ocurrence } from "@/types/Ocurrence";
import { motion, AnimatePresence } from "framer-motion";
import { getTimeAgo } from "@/utils/getTimeAgo";
import { getStatusColor } from "@/utils/getStatusColor";
import { convertStatusToLabel } from "@/utils/convertStatusToLabel";
import { formatDateTime } from "@/utils/formatDateTime";
import {
  calculateSLA,
  getSLADaysRemaining,
  getSLAStatus,
} from "@/utils/calculateSLA";
import { Spinner } from "@/components/Spinner";
import toast from "react-hot-toast";
import axios from "axios";
import { DynamicIcon } from "@/components/DynamicIcon";
import { reverseGeocode, isCoordinateAddress } from "@/utils/reverseGeocode";

/**
 * IMPORTANTE: A configuração de 'libraries' deve ser consistente em todos os componentes
 * que usam Google Maps. O Google Maps Loader não permite ser chamado com diferentes opções.
 * Mantenha sempre a mesma configuração em:
 * - LocationMap
 * - WorkOrderMap
 * - OccurrenceDetails
 * Se precisar adicionar novas libraries, adicione em TODOS os componentes.
 */
const libraries: ("places" | "drawing" | "geometry")[] = ["places"];

// Function to format text with markdown-like syntax (similar to WhatsApp)
const formatMessageContent = (text: string) => {
  const parts: React.ReactNode[] = [];
  let currentIndex = 0;
  let partKey = 0;

  // Split by ** for bold text
  const boldPattern = /\*\*(.*?)\*\*/g;
  let match;
  let lastIndex = 0;

  while ((match = boldPattern.exec(text)) !== null) {
    // Add text before the bold part
    if (match.index > lastIndex) {
      const beforeText = text.substring(lastIndex, match.index);
      parts.push(<span key={`text-${partKey++}`}>{beforeText}</span>);
    }

    // Add the bold part
    parts.push(
      <strong key={`bold-${partKey++}`} className="font-bold">
        {match[1]}
      </strong>,
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    parts.push(
      <span key={`text-${partKey++}`}>{text.substring(lastIndex)}</span>,
    );
  }

  return parts.length > 0 ? parts : text;
};

export const OccurrenceDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const passedOccurrence = location.state?.occurrence as Ocurrence | undefined;
  const [replyContent, setReplyContent] = useState("");
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [isEvaluationModalOpen, setIsEvaluationModalOpen] = useState(false);
  const [isReopenModalOpen, setIsReopenModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const createComment = useCreateComment();
  const generatePresignedUrl = useGeneratePresignedUrl();
  const { data: canEvaluateData } = useCanEvaluate(id || "");
  const { data: evaluations } = useGetEvaluationsByOccurrence(id || "");
  const { data: canReopenData } = useCanReopenOcurrence(id || "");
  
  // Hooks para funcionalidade de acompanhar ocorrência
  const { data: currentUser } = useGetCurrentUser();
  const { data: isFollowingData, isLoading: isLoadingFollowStatus } = useIsFollowingOcurrence(id || "");
  const followOcurrence = useFollowOcurrence();
  const unfollowOcurrence = useUnfollowOcurrence();

  const { data: occurrence, isLoading } = useGetOcurrenceById(id || "", {
    initialData: passedOccurrence,
  });

  // Debug: Log evaluation conditions
  console.log("🔍 Debug Avaliação:", {
    status: occurrence?.status,
    canEvaluate: canEvaluateData?.canEvaluate,
    canEvaluateData: canEvaluateData,
    evaluationsLength: evaluations?.length,
    evaluations: evaluations,
    shouldShowButton:
      occurrence?.status === "RESOLVED" &&
      canEvaluateData?.canEvaluate &&
      !evaluations?.length,
  });

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [mapCenter, setMapCenter] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [resolvedAddress, setResolvedAddress] = useState<string | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",
    libraries,
    language: "pt-BR",
    region: "BR",
  });

  // Usar lat/lng diretamente se disponíveis, senão geocodificar o endereço
  useEffect(() => {
    if (!occurrence) return;

    const lat = occurrence.latitude ? parseFloat(occurrence.latitude) : NaN;
    const lng = occurrence.longitude ? parseFloat(occurrence.longitude) : NaN;

    if (!isNaN(lat) && !isNaN(lng)) {
      setMapCenter({ lat, lng });

      // Só fazer reverse geocoding se o endereço da API for apenas coordenadas
      if (isCoordinateAddress(occurrence.address)) {
        reverseGeocode(lat, lng).then(setResolvedAddress);
      }
    } else if (occurrence.address && isLoaded && typeof google !== "undefined") {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: occurrence.address }, (results, status) => {
        if (status === "OK" && results?.[0]) {
          setMapCenter({
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
          });
        }
      });
    }
  }, [occurrence?.address, occurrence?.latitude, occurrence?.longitude, isLoaded]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((file) => {
      const isValidType =
        file.type.startsWith("image/") ||
        file.type.startsWith("video/") ||
        file.type === "application/pdf";
      const isValidSize = file.size <= 20 * 1024 * 1024; // 20MB

      if (!isValidType) {
        toast.error(
          `${file.name}: Apenas imagens, vídeos e PDFs são permitidos`,
        );
        return false;
      }
      if (!isValidSize) {
        toast.error(`${file.name}: Arquivo deve ter no máximo 20MB`);
        return false;
      }
      return true;
    });

    setReplyFiles((prev) => [...prev, ...validFiles]);

    // Reset input para permitir selecionar os mesmos arquivos novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    setReplyFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitReply = async () => {
    if (!replyContent.trim()) {
      toast.error("Digite uma mensagem");
      return;
    }

    setIsSubmittingReply(true);

    try {
      // Upload de arquivos anexados
      const attachments = [];

      for (const file of replyFiles) {
        // Determina o tipo correto: IMAGE, VIDEO ou DOCUMENT
        let fileType: "IMAGE" | "VIDEO" | "DOCUMENT";
        if (file.type.startsWith("image/")) {
          fileType = "IMAGE";
        } else if (file.type.startsWith("video/")) {
          fileType = "VIDEO";
        } else {
          fileType = "DOCUMENT";
        }

        // Gera URL pré-assinada
        const presignedData = await generatePresignedUrl.mutateAsync({
          type: fileType,
          fileName: file.name,
          mimeType: file.type,
          size: file.size,
        });

        // Upload do arquivo
        await axios.put(presignedData.uploadUrl, file, {
          headers: {
            "Content-Type": file.type,
          },
        });

        attachments.push({
          key: presignedData.key,
          fileName: file.name,
          mimeType: file.type,
          size: file.size,
        });
      }

      // Envia o comentário
      await createComment.mutateAsync({
        occurrenceId: occurrence!.id,
        data: {
          type: "CITIZEN_RESPONSE",
          content: replyContent,
          attachments,
        },
      });

      toast.success("Resposta enviada com sucesso!");
      setReplyContent("");
      setReplyFiles([]);
    } catch (error) {
      console.error("Erro ao enviar resposta:", error);
      toast.error("Erro ao enviar resposta. Tente novamente.");
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? (occurrence?.media?.length || 1) - 1 : prev - 1,
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === (occurrence?.media?.length || 1) - 1 ? 0 : prev + 1,
    );
  };

  if (isLoading) {
    return (
      <div className="bg-[#f5f5f5] min-h-screen pb-24 pt-16 flex items-center justify-center">
        <div className="text-center">
          <Spinner className="mx-auto text-[hsl(var(--primary))]" />
          <p className="mt-4 text-gray-600">Carregando detalhes...</p>
        </div>
      </div>
    );
  }

  if (!occurrence) {
    return (
      <div className="bg-[#f5f5f5] min-h-screen pb-24 pt-16 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Ocorrência não encontrada</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-6 py-2 bg-[hsl(var(--primary))] text-white rounded-xl"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f5f5f5] min-h-screen pb-24 pt-16">
      <div className="bg-white shadow-sm">
        <div className="flex items-center justify-between gap-3 p-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <IoArrowBackOutline className="text-2xl text-gray-700" />
            </button>
            <h1 className="text-lg md:text-xl font-bold text-gray-800 truncate">
              Detalhes da ocorrência
            </h1>
            {canReopenData && canReopenData.reopenCount > 0 && (
              <span className="flex-shrink-0 px-3 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
                Reaberta {canReopenData.reopenCount}x
              </span>
            )}
          </div>
          <div className="flex-shrink-0">
            <BadgeStatus
              title={convertStatusToLabel(occurrence.status)}
              color={getStatusColor(occurrence.status)}
            />
          </div>
        </div>
      </div>

      <div className="px-4 space-y-4 mt-4">
        <div className="rounded-xl overflow-hidden max-w-3xl mx-auto">
          <div className="relative w-full aspect-video bg-gray-900">
            {occurrence.media?.[currentImageIndex] ? (
              <AnimatePresence mode="wait">
                {occurrence.media[currentImageIndex].type === "VIDEO" ? (
                  <motion.video
                    key={currentImageIndex}
                    src={
                      (import.meta.env.VITE_BUCKET_URL || "") +
                      "/" +
                      occurrence.media[currentImageIndex].key +
                      "#t=0.1"
                    }
                    className="absolute inset-0 w-full h-full object-cover"
                    controls
                    playsInline
                    preload="metadata"
                    muted
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  />
                ) : (
                  <motion.img
                    key={currentImageIndex}
                    src={
                      (import.meta.env.VITE_BUCKET_URL || "") +
                      "/" +
                      occurrence.media[currentImageIndex].key
                    }
                    alt={`Foto ${currentImageIndex + 1}`}
                    className="absolute inset-0 w-full h-full object-cover"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  />
                )}
              </AnimatePresence>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white text-lg">
                Sem mídia disponível
              </div>
            )}

            {occurrence.media && occurrence.media.length > 1 && (
              <>
                <button
                  onClick={handlePreviousImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors"
                >
                  <IoChevronBackOutline className="text-white text-xl" />
                </button>
                <button
                  onClick={handleNextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors"
                >
                  <IoChevronForwardOutline className="text-white text-xl" />
                </button>

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {occurrence.media?.map((_: any, index: any) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentImageIndex
                          ? "bg-white w-6"
                          : "bg-white/50"
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* SLA Card */}
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-5">
          <div className="flex items-start gap-3">
            {(() => {
              const slaStatus = getSLAStatus(
                occurrence.updatedAt,
                occurrence.userPerceivedUrgency,
              );
              const { days, isOverdue } = getSLADaysRemaining(
                occurrence.updatedAt,
                occurrence.userPerceivedUrgency,
              );

              const iconColor =
                slaStatus === "safe"
                  ? "text-[hsl(var(--primary))]"
                  : slaStatus === "warning"
                    ? "text-yellow-500"
                    : "text-red-500";

              const textColor =
                slaStatus === "safe"
                  ? "text-[hsl(var(--primary))]"
                  : slaStatus === "warning"
                    ? "text-yellow-700"
                    : "text-red-700";

              return (
                <>
                  <IoTimerOutline className={`text-2xl ${iconColor} mt-0.5`} />
                  <div>
                    <p className="text-sm text-gray-500">
                      Previsão de atendimento (SLA)
                    </p>
                    <p className={`text-base font-semibold ${textColor}`}>
                      {calculateSLA(
                        occurrence.updatedAt,
                        occurrence.userPerceivedUrgency,
                      )}
                    </p>
                    <p className={`text-xs mt-0.5 font-medium ${textColor}`}>
                      {isOverdue
                        ? `⚠️ Atrasado há ${days} ${days === 1 ? "dia" : "dias"}`
                        : slaStatus === "danger"
                          ? `⏰ Vence hoje!`
                          : slaStatus === "warning"
                            ? `⏳ Faltam ${days} ${days === 1 ? "dia" : "dias"}`
                            : `✓ Faltam ${days} ${days === 1 ? "dia" : "dias"}`}
                    </p>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        {/* Follow/Unfollow Button - Only for occurrences that don't belong to current user */}
        {currentUser && occurrence.userId !== currentUser.id && !isLoadingFollowStatus && (
          <div className={`rounded-xl shadow-sm p-6 border ${
            isFollowingData?.isFollowing 
              ? "bg-gradient-to-r from-green-50 to-green-100/50 border-green-200"
              : "bg-gradient-to-r from-blue-50 to-blue-100/50 border-blue-200"
          }`}>
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                isFollowingData?.isFollowing 
                  ? "bg-green-500/20"
                  : "bg-blue-500/20"
              }`}>
                {isFollowingData?.isFollowing ? (
                  <IoEyeOffOutline className="text-2xl text-green-600" />
                ) : (
                  <IoEyeOutline className="text-2xl text-blue-600" />
                )}
              </div>
              <div className="flex-1">
                {isFollowingData?.isFollowing ? (
                  <>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      Você está acompanhando esta ocorrência
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Você receberá notificações sobre atualizações e mudanças de status.
                    </p>
                    <button
                      onClick={async () => {
                        try {
                          await unfollowOcurrence.mutateAsync(id || "");
                          toast.success("Você deixou de acompanhar esta ocorrência");
                        } catch {
                          toast.error("Erro ao deixar de acompanhar. Tente novamente.");
                        }
                      }}
                      disabled={unfollowOcurrence.isPending}
                      className="px-6 py-2.5 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors shadow-sm disabled:opacity-50"
                    >
                      {unfollowOcurrence.isPending ? "Processando..." : "Deixar de Acompanhar"}
                    </button>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      Acompanhar esta ocorrência?
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Você poderá acompanhar o andamento e receber notificações sobre 
                      atualizações, mesmo não sendo o autor desta ocorrência.
                    </p>
                    <button
                      onClick={async () => {
                        try {
                          await followOcurrence.mutateAsync(id || "");
                          toast.success("Você agora está acompanhando esta ocorrência!");
                        } catch {
                          toast.error("Erro ao acompanhar. Tente novamente.");
                        }
                      }}
                      disabled={followOcurrence.isPending}
                      className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50"
                    >
                      {followOcurrence.isPending ? "Processando..." : "Acompanhar Ocorrência"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Reopen Button - Only for RESOLVED status and not FILED */}
        {occurrence.status === "RESOLVED" &&
          occurrence.status !== "FILED" &&
          canReopenData?.canReopen && (
            <div className="bg-gradient-to-r from-orange-50 to-orange-100/50 rounded-xl shadow-sm p-6 border border-orange-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                  <IoRefreshOutline className="text-2xl text-orange-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    Problema não foi resolvido?
                  </h3>
                  <p className="text-sm text-gray-600 mb-1">
                    Você pode reabrir esta ocorrência se considerar que o
                    problema não foi solucionado adequadamente.
                  </p>
                  {canReopenData?.daysRemaining !== null && (
                    <span className="block mt-1 text-xs text-gray-500">
                      Você tem {canReopenData.daysRemaining}{" "}
                      {canReopenData.daysRemaining === 1 ? "dia" : "dias"} para
                      reabrir
                    </span>
                  )}
                  {canReopenData?.reopenCount > 0 && (
                    <span className="block mt-1 text-xs text-orange-700 font-medium">
                      ⚠️ Já reaberta {canReopenData.reopenCount}{" "}
                      {canReopenData.reopenCount === 1 ? "vez" : "vezes"}{" "}
                      (máximo: {canReopenData.maxReopens})
                    </span>
                  )}
                  <button
                    onClick={() => setIsReopenModalOpen(true)}
                    className="mt-4 px-6 py-2.5 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors shadow-sm"
                  >
                    Reabrir Ocorrência
                  </button>
                </div>
              </div>
            </div>
          )}

        {/* Evaluation Button - Only for RESOLVED status */}
        {occurrence.status === "RESOLVED" &&
          canEvaluateData?.canEvaluate &&
          !evaluations?.length && (
            <div className="bg-gradient-to-r from-[hsl(var(--primary))]/10 to-[hsl(var(--primary))]/5 rounded-xl shadow-sm p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[hsl(var(--primary))]/20 flex items-center justify-center flex-shrink-0">
                  <IoStarOutline className="text-2xl text-[hsl(var(--primary))]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    Avalie nosso atendimento
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Sua opinião é muito importante para melhorarmos nossos
                    serviços.
                    {canEvaluateData?.daysUntilDeadline && (
                      <span className="block mt-1 text-xs text-gray-500">
                        Você tem {canEvaluateData.daysUntilDeadline} dias para
                        avaliar
                      </span>
                    )}
                  </p>
                  <button
                    onClick={() => setIsEvaluationModalOpen(true)}
                    className="px-6 py-2.5 bg-[hsl(var(--primary))] text-white rounded-lg font-medium hover:bg-[hsl(var(--primary))]/90 transition-colors shadow-sm"
                  >
                    Avaliar Atendimento
                  </button>
                </div>
              </div>
            </div>
          )}

        {/* Show existing evaluation */}
        {evaluations && evaluations.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <IoStarOutline className="text-xl" />
              Sua Avaliação
            </h3>
            {evaluations.map((evaluation) => (
              <div key={evaluation.id} className="space-y-3">
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <IoStarOutline
                      key={star}
                      className={`text-2xl ${
                        star <= evaluation.rating
                          ? "text-yellow-400 fill-yellow-400"
                          : "text-gray-300"
                      }`}
                    />
                  ))}
                  <span className="text-sm text-gray-500 ml-2">
                    {formatDateTime(evaluation.createdAt)}
                  </span>
                </div>
                {evaluation.comment && (
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                    {evaluation.comment}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Informações</h2>

          <div className="flex items-start gap-3">
            <IoDocumentTextOutline className="text-2xl text-gray-400 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Protocolo</p>
              <p className="text-base font-semibold text-gray-800">
                {occurrence.protocol}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            {occurrence.category.icon && occurrence.category.lib ? (
              <div className="text-2xl text-gray-400 mt-0.5">
                <DynamicIcon
                  iconName={occurrence.category.icon}
                  lib={occurrence.category.lib}
                  size={24}
                />
              </div>
            ) : (
              <IoAlertCircleOutline className="text-2xl text-gray-400 mt-0.5" />
            )}
            <div>
              <p className="text-sm text-gray-500">Categoria</p>
              <p className="text-base font-semibold text-gray-800">
                {occurrence.category.name} - {occurrence.subcategory.name}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <IoSpeedometerOutline className="text-2xl text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-500">Descrição</p>
              <p className="text-base text-gray-800 break-words">
                {occurrence.description}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <IoLocationOutline className="text-2xl text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-500">Endereço</p>
              <p className="text-base font-semibold text-gray-800 break-words">
                {resolvedAddress || occurrence.address}
              </p>
            </div>
          </div>

          {isLoaded && mapCenter && (
            <div className="mt-4 rounded-lg overflow-hidden border border-gray-200 h-[250px]">
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={mapCenter}
                zoom={16}
                options={{
                  zoomControl: true,
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                }}
              >
                <Marker position={mapCenter} />
              </GoogleMap>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div className="flex items-start gap-3">
              <IoCalendarOutline className="text-2xl text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Data de abertura</p>
                <p className="text-base font-semibold text-gray-800">
                  {formatDateTime(occurrence.createdAt)}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {getTimeAgo(occurrence.createdAt)}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <IoTimeOutline className="text-2xl text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Última atualização</p>
                <p className="text-base font-semibold text-gray-800">
                  {formatDateTime(occurrence.updatedAt)}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {getTimeAgo(occurrence.updatedAt)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {occurrence.timeline &&
          occurrence.timeline.filter((e: any) => e.eventType !== "comment")
            .length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">
                Histórico
              </h2>

              <div className="space-y-4">
                {occurrence.timeline
                  .filter((e: any) => e.eventType !== "comment")
                  .map((event: any, index: number) => (
                    <div key={event.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            index === 0
                              ? "bg-[hsl(var(--primary))]/10"
                              : "bg-gray-100"
                          }`}
                        >
                          <IoCheckmarkCircleOutline
                            className={`text-xl ${
                              index === 0
                                ? "text-[hsl(var(--primary))]"
                                : "text-gray-400"
                            }`}
                          />
                        </div>
                        {index < occurrence.timeline!.length - 1 && (
                          <div className="w-0.5 flex-1 bg-gray-200 mt-2 min-h-[40px]" />
                        )}
                      </div>

                      <div className="flex-1 pb-4">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h3 className="font-semibold text-gray-800 text-sm md:text-base">
                            {event.fromStatus
                              ? `${convertStatusToLabel(event.fromStatus)} → ${convertStatusToLabel(event.toStatus)}`
                              : convertStatusToLabel(event.toStatus)}
                          </h3>
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {formatDateTime(event.timestamp)}
                          </span>
                        </div>

                        {event.notes && (
                          <p className="text-sm text-gray-600 mt-1 break-words whitespace-pre-wrap">
                            {event.notes}
                          </p>
                        )}

                        <div className="flex items-center gap-2 mt-2">
                          <BadgeStatus
                            title={
                              event.changedByRole === "CITIZEN"
                                ? "Cidadão"
                                : event.changedByRole === "ADMIN"
                                  ? "Administrador"
                                  : "Sistema"
                            }
                            color="gray"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

        {occurrence.comments &&
          occurrence.comments.filter(
            (c: any) => c.type === "PUBLIC" || c.type === "CITIZEN_RESPONSE",
          ).length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <IoChatbubbleEllipsesOutline className="text-xl" />
                Mensagens
              </h2>

              <div className="space-y-4 mb-4 max-h-[500px] overflow-y-auto pr-1">
                {occurrence.comments
                  .filter(
                    (comment: any) =>
                      comment.type === "PUBLIC" ||
                      comment.type === "CITIZEN_RESPONSE",
                  )
                  .sort(
                    (a: any, b: any) =>
                      new Date(a.createdAt).getTime() -
                      new Date(b.createdAt).getTime(),
                  )
                  .map((comment: any) => {
                    const isCitizen = comment.type === "CITIZEN_RESPONSE";
                    const sectorName =
                      comment.author.sectorMemberships?.[0]?.name ||
                      "Prefeitura";
                    const authorName =
                      comment.author.profile?.fullName || comment.author.email;
                    const displayName = isCitizen
                      ? currentUser?.email || "Você"
                      : authorName;
                    const initial = (
                      displayName[0] || "U"
                    ).toUpperCase();
                    const roleLabel = isCitizen
                      ? "Cidadão"
                      : sectorName;
                    const dateStr = new Date(
                      comment.createdAt,
                    ).toLocaleString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    });

                    return (
                      <div
                        key={comment.id}
                        className={`flex ${isCitizen ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-xl px-4 py-3 border ${
                            isCitizen
                              ? "bg-[hsl(var(--primary))]/5 border-[hsl(var(--primary))]/20"
                              : "bg-white border-gray-200"
                          }`}
                        >
                          {/* Header: avatar + nome + role + data */}
                          <div className="flex items-center gap-2.5 mb-2">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-white flex-shrink-0 ${
                                isCitizen
                                  ? "bg-[hsl(var(--primary))]"
                                  : "bg-gray-400"
                              }`}
                            >
                              {initial}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap min-w-0">
                              <span className="font-semibold text-sm text-gray-800 truncate">
                                {displayName}
                              </span>
                              <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200 whitespace-nowrap">
                                {roleLabel}
                              </span>
                              <span className="text-xs text-gray-400 whitespace-nowrap">
                                {dateStr}
                              </span>
                            </div>
                          </div>
                          {/* Conteúdo */}
                          <p className="text-sm text-gray-700 break-words whitespace-pre-wrap pl-[42px]">
                            {formatMessageContent(comment.content)}
                          </p>

                          {comment.attachments &&
                            comment.attachments.length > 0 && (
                              <div className="mt-2 flex gap-2 flex-wrap pl-[42px]">
                                {comment.attachments.map((attachment: any) => (
                                  <a
                                    key={attachment.id}
                                    href={
                                      (import.meta.env.VITE_BUCKET_URL || "") +
                                      "/" +
                                      attachment.key
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 text-xs transition-colors"
                                  >
                                    <IoDocumentAttachOutline className="text-gray-500" />
                                    <span className="text-gray-700 truncate max-w-[150px]">
                                      {attachment.fileName}
                                    </span>
                                  </a>
                                ))}
                              </div>
                            )}
                        </div>
                      </div>
                    );
                  })}
              </div>

              {/* Formulário de resposta - apenas para o dono da ocorrência */}
              {currentUser && occurrence.userId === currentUser.id && 
               ((occurrence.canCitizenReply ?? true) ||
                occurrence.status === "RETURNED") && (
                <div className="border-t border-gray-200 pt-3">
                  {replyFiles.length > 0 && (
                    <div className="flex gap-2 flex-wrap mb-2">
                      {replyFiles.map((file: File, index: number) => (
                        <div
                          key={index}
                          className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-lg border border-gray-200 text-xs"
                        >
                          <IoDocumentAttachOutline className="text-gray-500" />
                          <span className="text-gray-700 max-w-[150px] truncate">
                            {file.name}
                          </span>
                          <button
                            onClick={() => handleRemoveFile(index)}
                            className="p-0.5 hover:bg-gray-200 rounded-full"
                            disabled={isSubmittingReply}
                          >
                            <IoCloseOutline className="text-sm text-gray-600" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-end gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,video/*,application/pdf"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={isSubmittingReply}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      disabled={isSubmittingReply}
                      title="Anexar arquivo"
                    >
                      <IoAttachOutline className="text-xl" />
                    </button>

                    <div className="flex-1">
                      <textarea
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            if (replyContent.trim()) {
                              handleSubmitReply();
                            }
                          }
                        }}
                        placeholder="Digite sua mensagem..."
                        rows={1}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/30 focus:border-[hsl(var(--primary))] resize-none max-h-[100px]"
                        disabled={isSubmittingReply}
                      />
                    </div>

                    <button
                      onClick={handleSubmitReply}
                      disabled={isSubmittingReply || !replyContent.trim()}
                      className="p-2 text-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]/80 transition-colors disabled:opacity-30"
                      title="Enviar mensagem"
                    >
                      {isSubmittingReply ? (
                        <Spinner className="w-5 h-5" />
                      ) : (
                        <IoSendOutline className="text-xl" />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
      </div>

      {/* Evaluation Modal */}
      <EvaluationModal
        occurrenceId={occurrence.id}
        occurrenceProtocol={occurrence.protocol}
        isOpen={isEvaluationModalOpen}
        onClose={() => setIsEvaluationModalOpen(false)}
      />

      {/* Reopen Modal */}
      <ReopenModal
        occurrenceId={occurrence.id}
        occurrenceProtocol={occurrence.protocol}
        isOpen={isReopenModalOpen}
        onClose={() => setIsReopenModalOpen(false)}
        maxReopens={canReopenData?.maxReopens || 2}
        currentReopens={canReopenData?.reopenCount || 0}
      />
    </div>
  );
};
